import debounce from 'lodash.debounce';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { SingleValue } from 'react-select';
import AsyncSelect from 'react-select/async';
import { getElements } from '../../../../config/graphElements';
import { MAP_ELEMENT_TYPES } from '../../../../constants/graph';
import { TAB_TYPES } from '../../../../constants/tab';
import { getFlowSharedElements } from '../../../../sources/flow';
import type { MapElementRequestAPI } from '../../../../sources/graph';
import { getPageList, savePage } from '../../../../sources/page';
import { ElementType } from '../../../../types/element';
import type { SelectOption } from '../../../../types/generic';
import type { PageElementRequestAPI } from '../../../../types/page';
import { getByID } from '../../../../utils/collection';
import { isNullOrEmpty } from '../../../../utils/guard';
import { generateRouteUrl } from '../../../../utils/routing';
import { getSharedStyles } from '../../../../utils/select';
import { stringContains } from '../../../../utils/string';
import ButtonDefault from '../../../buttons/ButtonDefault';
import ButtonPrimary from '../../../buttons/ButtonPrimary';
import { useFeatureFlag } from '../../../featureFlags/FeatureFlagProvider';
import FormGroup from '../../../generic/FormGroup';
import Glyphicon from '../../../generic/Glyphicon';
import HTMLInput from '../../../generic/HTMLEditor/HTMLInput';
import ModalBody from '../../../generic/modal/ModalBody';
import ModalFooter from '../../../generic/modal/ModalFooter';
import Footer from '../common/Footer';
import NameInput from '../common/NameInput';
import ListenerList from '../common/listeners/ListenerList';
import MessageActionsList from '../common/messageActions/MessageActionsList';
import NavigationOverrideList from '../common/navigationOverrides/NavigationOverrideList';
import OutcomeList from '../common/outcomes/OutcomeList';
import WaitList from '../common/wait/WaitList';
import { useMapElement } from '../contextProviders/MapElementProvider';
import { useNotificationDispatch } from '../../../notifications/NotificationProvider';

const PageConfiguration = () => {
    const [hasSubmitted, setHasSubmitted] = useState(false);
    const [onlyShowPagesInFlow, setOnlyShowPagesInFlow] = useState(true);
    const [selectedPage, setSelectedPage] = useState<SelectOption | null>(null);

    const [queryPagesWithinFlowIsLoading, setQueryPagesWithinFlowIsLoading] = useState(false);
    const [queryAllFlowsIsLoading, setQueryAllFlowsIsLoading] = useState(false);

    const navigate = useNavigate();

    const { getFlag } = useFeatureFlag();

    const {
        flowId,
        mapElement,
        setMapElement,
        onUpdateName,
        onUpdateTitle,
        onSaveMapElement,
        onClose,
        onUpdateNotAuthorizedMessage,
        onUpdateComments,
    } = useMapElement();

    const dispatchNotification = useNotificationDispatch();

    let pageElementId: string | null | undefined;
    let pageElementDeveloperName: string | null | undefined;
    if (mapElement) {
        pageElementId = mapElement.pageElementId;
        pageElementDeveloperName = mapElement.pageElementDeveloperName;
    }
    useEffect(() => {
        if (pageElementId && pageElementDeveloperName) {
            setSelectedPage({
                value: pageElementId,
                label: pageElementDeveloperName,
            });
        } else {
            setSelectedPage(null);
        }
    }, [pageElementId, pageElementDeveloperName]);

    if (!mapElement) {
        onClose();
        return;
    }
    const { developerName, developerSummary, title } = mapElement;

    const isPageElementNameValid = !isNullOrEmpty(developerName);
    const isSelectedPageValid = !isNullOrEmpty(selectedPage);
    const graphElements = getElements(getFlag);

    const createNewPage = () => {
        const newPagePayload: PageElementRequestAPI = {
            id: null,
            developerName: isNullOrEmpty(developerName) ? 'New Page' : developerName,
            developerSummary: null,
            elementType: ElementType.Page,
            pageContainers: [
                {
                    id: null,
                    developerName: 'Main',
                    containerType: 'VERTICAL_FLOW',
                },
            ],
            pageConditions: null,
            pageComponents: null,
        };
        return savePage(newPagePayload);
    };

    const onNewPageClick = async () => {
        try {
            const page = await createNewPage();

            onSelectPage({ value: page.id, label: page.developerName });

            const routeOptions = {
                tabType: TAB_TYPES.page,
                options: {
                    pageName: page.developerName,
                    elementId: page.id,
                },
            };

            const route = generateRouteUrl(routeOptions);

            navigate(route);
        } catch (error) {
            dispatchNotification({
                type: 'notifyError',
                payload: {
                    error,
                },
            });
        }
    };

    const onEditPageClick = () => {
        if (selectedPage?.value) {
            const route = generateRouteUrl({
                tabType: TAB_TYPES.page,
                options: {
                    elementId: selectedPage.value,
                },
            });

            navigate(route);
        }
    };

    const onSelectPage = (option: SingleValue<SelectOption>) => {
        setMapElement({
            ...mapElement,
            pageElementId: option?.value ?? null,
            pageElementDeveloperName: option?.label ?? null,
        });
    };

    const onSave = () => {
        setHasSubmitted(true);
        if (isPageElementNameValid && isSelectedPageValid) {
            onSaveMapElement(mapElement as MapElementRequestAPI);
        }
    };

    const queryAllPages = debounce(
        async (searchQuery: string, callback: (options: SelectOption[]) => void) => {
            setQueryAllFlowsIsLoading(true);

            try {
                const response = await getPageList({
                    search: searchQuery,
                    page: 1,
                    pageSize: 100,
                    orderBy: 'developerName',
                    orderDirection: 'ASC',
                });

                callback(
                    response.items.map<SelectOption>((r) => ({
                        value: r.id,
                        label: r.developerName,
                    })),
                );
            } catch (error) {
                dispatchNotification({
                    type: 'notifyError',
                    payload: {
                        error,
                    },
                });
            } finally {
                setQueryAllFlowsIsLoading(false);
            }
        },
        500,
    ) as (inputValue: string, callback: (options: SelectOption[]) => void) => void;

    const queryPagesWithinFlow = debounce(
        async (searchQuery: string, callback: (options: SelectOption[]) => void) => {
            setQueryPagesWithinFlowIsLoading(true);

            try {
                const response = await getFlowSharedElements(flowId, 'page');
                response
                    .filter((page) => stringContains(page.developerName, searchQuery))
                    .sort((a, b) => a.developerName.localeCompare(b.developerName));

                callback(
                    response.map<SelectOption>((r) => ({
                        value: r.id,
                        label: r.developerName,
                    })),
                );
            } catch (error) {
                dispatchNotification({
                    type: 'notifyError',
                    payload: {
                        error,
                    },
                });
            } finally {
                setQueryPagesWithinFlowIsLoading(false);
            }
        },
        500,
    ) as (inputValue: string, callback: (options: SelectOption[]) => void) => void;

    const renderBody = () => (
        <>
            <NameInput
                isValid={isPageElementNameValid}
                showValidation={hasSubmitted}
                id="page-element-name"
                name={mapElement.developerName ?? ''}
                onUpdateName={onUpdateName}
            />
            {mapElement.elementType.toLowerCase() === MAP_ELEMENT_TYPES.modal && (
                <FormGroup label="Title" htmlFor={'title'}>
                    <input
                        id={'title'}
                        value={title ?? ''}
                        onChange={(e) => onUpdateTitle(e.target.value)}
                        maxLength={255}
                        className="form-control form-control-width"
                        type="text"
                    />
                </FormGroup>
            )}
            <FormGroup
                isRequired
                label="Page Layout"
                htmlFor="select-page"
                className={'margin-top'}
                isValid={isSelectedPageValid}
                validationMessage={'Please select a Page Layout.'}
                showValidation={hasSubmitted}
            >
                <div className="flex">
                    {onlyShowPagesInFlow ? (
                        <AsyncSelect
                            key="queryFlow"
                            inputId="select-page"
                            className="flex-grow form-control-width"
                            placeholder="Search"
                            loadOptions={queryPagesWithinFlow}
                            onChange={onSelectPage}
                            value={selectedPage}
                            isLoading={queryPagesWithinFlowIsLoading}
                            isSearchable={true}
                            isClearable={true}
                            defaultOptions
                            styles={getSharedStyles<SelectOption>()}
                        />
                    ) : (
                        <AsyncSelect
                            key="queryAll"
                            inputId="select-page"
                            className="flex-grow form-control-width"
                            placeholder="Search"
                            loadOptions={queryAllPages}
                            onChange={onSelectPage}
                            value={selectedPage}
                            isLoading={queryAllFlowsIsLoading}
                            isSearchable={true}
                            isClearable={true}
                            defaultOptions
                            styles={getSharedStyles<SelectOption>()}
                        />
                    )}

                    {isSelectedPageValid && (
                        <ButtonDefault onClick={onEditPageClick} className={'margin-left-sml'}>
                            <Glyphicon glyph={'pencil'} /> Edit Layout
                        </ButtonDefault>
                    )}
                    <ButtonPrimary onClick={onNewPageClick} className={'margin-left'}>
                        <Glyphicon glyph={'plus'} />
                        New Layout
                    </ButtonPrimary>
                </div>
            </FormGroup>
            <div className={'from-group'}>
                <label>
                    <input
                        type={'checkbox'}
                        checked={onlyShowPagesInFlow}
                        onChange={({ target: { checked } }) => {
                            setOnlyShowPagesInFlow(checked);
                        }}
                    />
                    Only show Page Layouts already being used in this Flow
                </label>
            </div>
            <OutcomeList />
            <MessageActionsList />
            <ListenerList />
            <NavigationOverrideList />
            <WaitList />
            <h4>Feedback</h4>
            <FormGroup label="Message to show users that are not authorized to take action">
                <HTMLInput
                    contentValue={mapElement.notAuthorizedMessage ?? ''}
                    onChange={(userContent) => onUpdateNotAuthorizedMessage(userContent)}
                    hasLinkPlugin={true}
                    hasImagePlugin={true}
                    hasValuePickerPlugin={true}
                    height={250}
                />
            </FormGroup>
            <FormGroup
                htmlFor="message-comments"
                label={`Comments about this ${
                    getByID(mapElement.elementType.toLowerCase(), graphElements)?.name ?? ''
                }`}
            >
                <textarea
                    id="message-comments"
                    value={developerSummary ?? ''}
                    className="form-control form-textarea"
                    onChange={({ target: { value } }) => onUpdateComments(value)}
                />
            </FormGroup>
        </>
    );

    const renderFooter = () => {
        return <Footer save={onSave} cancel={onClose} />;
    };

    return (
        <>
            <ModalBody>{renderBody()}</ModalBody>
            <ModalFooter>{renderFooter()}</ModalFooter>
        </>
    );
};

export default PageConfiguration;
